import React from 'react';
import { ArrowRight, RefreshCcw, PowerOff, CheckCircle2, Clock } from "lucide-react";
import { Badge } from "@shared/ui/badge";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@shared/ui/accordion";
import { Tabs, TabsList, TabsTrigger } from "@shared/ui/tabs";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useConsumerRequests } from '../../../requests/hook';
import { useActivityLog, useRequestDetail } from '../../../shared/hook';
import { getLoginDataFromStorage } from '@shared/utils/loginUtils';
import { getRemoteUtilityId } from '@shared/utils/getUtilityId';
import { useTranslation } from 'react-i18next';

const AccordionDetailsContent = ({ item }: { item: any }) => {
  const { t } = useTranslation();
  const { remoteUtilityId } = getLoginDataFromStorage();
  const { data: requestDetail, isLoading } = useRequestDetail({
    remote_utility_id: remoteUtilityId,
    id: item.id,
  });

  const details = requestDetail?.result || item;

  if (isLoading) {
    return <div className="py-4 text-xs text-gray-500">{t("Loading...")}</div>;
  }

  const renderFields = () => {
    if (item.type === "Service Disconnection") {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
            <p className="text-[10px] font-bold text-gray-400 mb-1">{t("Reason")}</p>
            <p className="font-bold text-[13px] text-gray-900">{details?.utilitySupportRequest?.supportRequestType || t("N/A")}</p>
          </div>
          <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
            <p className="text-[10px] font-bold text-gray-400 mb-1">{t("Disconn. Date")}</p>
            <p className="font-bold text-[13px] text-gray-900">{details?.requestDate ? new Date(details.requestDate).toLocaleDateString() : t("N/A")}</p>
          </div>
          <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 md:col-span-2">
            <p className="text-[10px] font-bold text-gray-400 mb-1">{t("Notes")}</p>
            <p className="font-bold text-[13px] text-gray-900">{details?.utilitySupportRequest?.longDescription || t("N/A")}</p>
          </div>
        </div>
      );
    }

    if (item.type === "Service Reconnection") {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
            <p className="text-[10px] font-bold text-gray-400 mb-1">{t("Reason")}</p>
            <p className="font-bold text-[13px] text-gray-900">{details?.utilitySupportRequest?.supportRequestType || t("N/A")}</p>
          </div>
          <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
            <p className="text-[10px] font-bold text-gray-400 mb-1">{t("Reconnect Date")}</p>
            <p className="font-bold text-[13px] text-gray-900">{details?.requestDate ? new Date(details.requestDate).toLocaleDateString() : t("N/A")}</p>
          </div>
          <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 md:col-span-2">
            <p className="text-[10px] font-bold text-gray-400 mb-1">{t("Notes")}</p>
            <p className="font-bold text-[13px] text-gray-900">{details?.utilitySupportRequest?.longDescription || t("N/A")}</p>
          </div>
        </div>
      );
    }

    if (item.type === "Ownership Transfer") {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
            <p className="text-[10px] font-bold text-gray-400 mb-1">{t("Transfer Type")}</p>
            <p className="font-bold text-[13px] text-gray-900">{details?.utilitySupportRequest?.supportRequestType || t("N/A")}</p>
          </div>
          <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
            <p className="text-[10px] font-bold text-gray-400 mb-1">{t("Effective Date")}</p>
            <p className="font-bold text-[13px] text-gray-900">{details?.requestDate ? new Date(details.requestDate).toLocaleDateString() : t("N/A")}</p>
          </div>
          <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
            <p className="text-[10px] font-bold text-gray-400 mb-1">{t("Transfer To")}</p>
            <p className="font-bold text-[13px] text-gray-900">{details?.additionalData?.transferTo || t("N/A")}</p>
          </div>
          <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
            <p className="text-[10px] font-bold text-gray-400 mb-1">{t("Special Instructions")}</p>
            <p className="font-bold text-[13px] text-gray-900">{details?.additionalData?.specialInstructions || t("N/A")}</p>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      {renderFields()}
      <AccordionTimeline requestId={item.id} />
    </>
  );
};

const AccordionTimeline = ({ requestId }: { requestId: string }) => {
  const { t } = useTranslation();
  const { remoteUtilityId } = getLoginDataFromStorage();

  const { data: activitylog, isLoading } = useActivityLog({
    remote_utility_id: remoteUtilityId,
    id: requestId,
    module: "cx"
  });

  const events = activitylog?.results?.map((item: any) => ({
    title: item.title || t("Activity"),
    time: item.date && item.time 
      ? `${item.date} ${item.time}` 
      : item.date || t("Unknown Time"),
    desc: item.description || t("No description available.")
  })) || [];

  return (
    <div className="mt-8">
      <p className="text-[10px] font-bold text-gray-400 mb-4 tracking-wide">{t("Timeline")}</p>
      {isLoading ? (
        <p className="text-xs text-gray-500">{t("Loading timeline...")}</p>
      ) : events.length > 0 ? (
        <div className="relative border-l border-gray-200 ml-3 space-y-6">
          {events.map((ev: any, i: number) => (
            <div key={i} className="relative pl-6">
              <div className="absolute w-3.5 h-3.5 bg-blue-500 border-2 border-blue-100 rounded-full -left-[7px] top-1.5" />
              <p className="text-[13px] font-bold text-gray-900">{ev.title}</p>
              <p className="text-[12px] text-gray-600 font-medium mt-0.5">{ev.desc}</p>
              <p className="text-[11px] text-gray-400 mt-1">{ev.time}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-500">{t("No activity history available.")}</p>
      )}
    </div>
  );
};

function AccordionList({ items }: { items: any[] }) {
  const { t } = useTranslation();
  console.log("accordion list",items)
  if (items.length === 0) {
    return <div className="py-8 text-center text-gray-500 text-sm">{t("No requests found.")}</div>;
  }
  return (
    <Accordion type="single" collapsible className="space-y-3">
      {items.map((item) => (
        <AccordionItem 
          key={item.keyId || item.id} 
          value={item.keyId || item.id} 
          className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
        >
          <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-gray-50/50 transition-colors">
            <div className="flex items-start gap-4 text-left w-full pr-4">
              <div className={`w-10 h-10 rounded-xl ${item.iconBg} ${item.iconColor} flex items-center justify-center shrink-0 mt-0.5`}>
                {item.type === "Service Disconnection" ? <PowerOff className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
              </div>
              <div className="flex flex-col flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <span className="font-bold text-[14px] text-gray-900">{item.id}</span>
                  <Badge variant="secondary" className={`${item.badgeBg} ${item.badgeColor} border-0 font-bold text-[11px] px-2 py-0.5 rounded-full`}>
                    {item.type}
                  </Badge>
                  <Badge variant="secondary" className="bg-gray-100 text-gray-600 border-0 font-bold text-[11px] px-2 py-0.5 rounded-full">
                    {item.status}
                  </Badge>
                </div>
                <div className="flex flex-col text-[12px] font-medium text-gray-500">
                  <span>{item.title}</span>
                  <span className="text-gray-400 mt-0.5 flex items-center">
                    {item.dates.split(" - ")[0]} 
                    <CheckCircle2 className="w-3 h-3 text-green-500 mx-1.5" /> 
                    <span className="text-green-600 font-bold">{item.dates.split(" - ")[1]}</span>
                  </span>
                </div>
              </div>
            </div>
          </AccordionTrigger>
          
          <AccordionContent className="px-5 pb-5 pt-2 border-t border-gray-100 text-sm ml-14">
            <AccordionDetailsContent item={item} />
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

function AccordionTabContent({ requestType }: { requestType?: string }) {
  const { t } = useTranslation();
  const {id}=useParams();
  

  const { data: apiData, isLoading } = useConsumerRequests({
    page: 1,
    limit: 100,
    consumer_id: id,
    remote_utility_id: getRemoteUtilityId(),
    request_type: requestType,
  });

  const history = React.useMemo(() => {
    if (!apiData?.results || apiData.results.length === 0) return [];
    let filtered = apiData.results;
    

    
    if (filtered.length === 0) return [];

    return filtered.map((item: any, index: number) => {
      const typeStr = item.requestType || '';
      let typeLabel = typeStr;
      let iconBg = "bg-gray-50";
      let iconColor = "text-gray-500";
      let badgeBg = "bg-gray-50";
      let badgeColor = "text-gray-600";
      
      if (typeStr.includes('Disconnect')) {
        typeLabel = "Service Disconnection";
        iconBg = "bg-red-50";
        iconColor = "text-red-500";
        badgeBg = "bg-red-50";
        badgeColor = "text-red-600";
      } else if (typeStr.includes('Reconnect')) {
        typeLabel = "Service Reconnection";
        iconBg = "bg-green-50";
        iconColor = "text-green-500";
        badgeBg = "bg-green-50";
        badgeColor = "text-green-600";
      } else if (typeStr.includes('Transfer')) {
        typeLabel = "Ownership Transfer";
        iconBg = "bg-blue-50";
        iconColor = "text-blue-500";
        badgeBg = "bg-blue-50";
        badgeColor = "text-blue-600";
      }

      return {
        keyId: item.id || item.requestNo || `other-${index}`,
        id: item.requestNo || item.id || 'NA',
        type: typeLabel,
        status: item.statusDisplay || item.status || 'NA',
        title: `${item.requestTypeDisplay || item.requestType || 'NA'} · ${item.statusDisplay || 'NA'}`,
        dates: `${item.createdDate ? new Date(item.createdDate).toLocaleDateString() : 'NA'} - ${item.scheduledDate || 'NA'}`,
        iconBg,
        iconColor,
        badgeBg,
        badgeColor,
        reason: item.utilitySupportRequest?.supportRequestType || 'NA',
        requestDate: item.requestDate ? new Date(item.requestDate).toLocaleDateString() : 'NA',
        notes: item.utilitySupportRequest?.longDescription || 'NA',
        ...item
      };
    });
  }, [apiData, requestType]);

  if (isLoading) {
    return <div className="py-8 text-center text-gray-500 text-sm">{t("Loading...")}</div>;
  }

  return <AccordionList items={history} />;
}


export function OtherRequestsTab({ consumerInfo }: { consumerInfo?: any }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="py-6">
      {/* Raise a Request Section */}
      <div className="mb-10">
        <h3 className="text-[10px] font-bold text-gray-500 mb-3">{t("Raise A Request")}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div 
            className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:border-blue-200 transition-colors cursor-pointer group"
            onClick={() => {
              const params = new URLSearchParams({
                consumerId: consumerInfo?.selectedConsumerId?.toString() || "",
                isSubAccount: 'true'
              });
              navigate(`/dashboard/requests/create/transfer?${params.toString()}`);
            }}
          >
            <div className="w-8 h-8 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center mb-3">
              <RefreshCcw className="w-4 h-4 text-gray-600" />
            </div>
            <h4 className="font-bold text-sm text-gray-900 mb-1">{t("Transfer Ownership")}</h4>
            <p className="text-[11px] font-medium text-gray-500 mb-4 line-clamp-2">{t("Transfer this account to a new owner or tenant")}</p>
            <div className="flex items-center text-blue-600 text-[11px] font-bold group-hover:text-blue-700">
              {t("Open Form")} <ArrowRight className="w-3 h-3 ml-1" />
            </div>
          </div>
          
          <div 
            className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:border-blue-200 transition-colors cursor-pointer group"
            onClick={() => {
              const params = new URLSearchParams({
                consumerId: consumerInfo?.selectedConsumerId?.toString() || "",
                isSubAccount: 'true'
              });
              navigate(`/dashboard/requests/create/disconnect?${params.toString()}`);
            }}
          >
            <div className="w-8 h-8 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center mb-3">
              <PowerOff className="w-4 h-4 text-gray-600" />
            </div>
            <h4 className="font-bold text-sm text-gray-900 mb-1">{t("Request Disconnection")}</h4>
            <p className="text-[11px] font-medium text-gray-500 mb-4 line-clamp-2">{t("Disconnect service at this property")}</p>
            <div className="flex items-center text-blue-600 text-[11px] font-bold group-hover:text-blue-700">
              {t("Open Form")} <ArrowRight className="w-3 h-3 ml-1" />
            </div>
          </div>
          
          <div 
            className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:border-blue-200 transition-colors cursor-pointer group"
            onClick={() => {
              const params = new URLSearchParams({
                consumerId: consumerInfo?.selectedConsumerId?.toString() || "",
                isSubAccount: 'true'
              });
              navigate(`/dashboard/requests/create/reconnect?${params.toString()}`);
            }}
          >
            <div className="w-8 h-8 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center mb-3">
              <CheckCircle2 className="w-4 h-4 text-gray-600" />
            </div>
            <h4 className="font-bold text-sm text-gray-900 mb-1">{t("Request Reconnection")}</h4>
            <p className="text-[11px] font-medium text-gray-500 mb-4 line-clamp-2">{t("Restore service to this previously disconnected account")}</p>
            <div className="flex items-center text-blue-600 text-[11px] font-bold group-hover:text-blue-700">
              {t("Open Form")} <ArrowRight className="w-3 h-3 ml-1" />
            </div>
          </div>
        </div>
      </div>

      {/* Request History Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-gray-900">{t("Request History")}</h3>
          <span className="text-[11px] font-medium text-gray-400">{t("{{count}} requests", { count: 2 })}</span>
        </div>
        
        <Tabs 
          defaultValue="all" 
          disableUrlSync={true}
          tabComponents={{
            "all": {
              label: "All",
              component: <AccordionTabContent />
            },
            "transfer": {
              label: "Transfer",
              component: <AccordionTabContent requestType="Transfer" />
            },
            "disconnection": {
              label: "Disconnection",
              component: <AccordionTabContent requestType="Disconnection" />
            },
            "reconnection": {
              label: "Reconnection",
              component: <AccordionTabContent requestType="Reconnection" />
            }
          }}
        />
      </div>
    </div>
  );
}
